import React, { useCallback, useEffect, useState } from "react";

import {
  faCheckCircle,
  faCircleNotch,
  faTimesCircle,
} from "@fortawesome/pro-regular-svg-icons";

import { useTestCredentialById } from "src/api/agents/credentials";
import type {
  CredentialItem,
  CredentialTestResult,
  EnvCredentialConfig,
  ModelProvider,
} from "src/clients/agent-management";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";

type CredentialTestEnvironment = "production" | "sandbox";

type EnvironmentTestState =
  | { status: "pending" }
  | { status: "success"; result: CredentialTestResult }
  | { status: "error"; message: string };

const ENVIRONMENT_LABEL: Record<CredentialTestEnvironment, string> = {
  production: "Production credentials",
  sandbox: "Sandbox credentials",
};

const DEFAULT_BASE_URL: Partial<Record<ModelProvider, string>> = {
  openai: "https://api.openai.com/v1",
  anthropic: "https://api.anthropic.com/v1",
};

const hasSandboxCredentials = (credential?: CredentialItem): boolean =>
  credential?.env_configs?.sandbox !== undefined;

const describeTestTarget = (
  provider: ModelProvider,
  config: EnvCredentialConfig | undefined,
): string | null => {
  const cfg = config ?? {};
  if (provider === "openai" || provider === "anthropic") {
    const baseUrl =
      (typeof cfg.base_url === "string" && cfg.base_url) ||
      DEFAULT_BASE_URL[provider] ||
      null;
    return baseUrl ? `GET ${baseUrl}/models` : null;
  }
  if (provider === "google_vertex") {
    const parts: string[] = [];
    if (typeof cfg.project_id === "string" && cfg.project_id) {
      parts.push(`project ${cfg.project_id}`);
    }
    if (typeof cfg.location === "string" && cfg.location) {
      parts.push(`location ${cfg.location}`);
    }
    return parts.length > 0
      ? `Service account · ${parts.join(" · ")}`
      : "Service account validation";
  }
  return null;
};

const summarizeDetails = (
  details: CredentialTestResult["details"],
): string | null => {
  if (!details) return null;
  const errorDetail = details.error_detail;
  if (typeof errorDetail === "string" && errorDetail) {
    return errorDetail;
  }
  return null;
};

const formatDetailValue = (value: unknown): string => {
  if (value === null) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(formatDetailValue).join(", ");
  return String(value);
};

// All diagnostic keys the backend sends (models_available, has_custom_base_url,
// project_id, location, …) must be reachable in the UI. error_detail is
// excluded because it is already rendered prominently in the result line.
const listDetailEntries = (
  details: CredentialTestResult["details"],
): [string, unknown][] =>
  Object.entries(details ?? {}).filter(([key]) => key !== "error_detail");

type Props = {
  credential?: CredentialItem;
  onClose: () => void;
};

const StatusIcon: React.FC<{ state: EnvironmentTestState }> = ({ state }) => {
  if (state.status === "pending") {
    return <Icon icon={faCircleNotch} size="sm" spin />;
  }
  if (state.status === "error") {
    return <Icon icon={faTimesCircle} size="sm" variant="error" />;
  }
  return state.result.success ? (
    <Icon icon={faCheckCircle} size="sm" variant="success" />
  ) : (
    <Icon icon={faTimesCircle} size="sm" variant="error" />
  );
};

const EnvironmentRow: React.FC<{
  environment: CredentialTestEnvironment;
  state: EnvironmentTestState;
  target: string | null;
}> = ({ environment, state, target }) => {
  const renderResultLine = () => {
    if (state.status === "pending") {
      return (
        <p className="font-inter-normal-12px text-gray-500">
          Testing connectivity…
        </p>
      );
    }
    if (state.status === "error") {
      return (
        <p className="font-inter-normal-12px text-red-600">{state.message}</p>
      );
    }
    const { result } = state;
    const summary = summarizeDetails(result.details);
    const detailEntries = listDetailEntries(result.details);
    return (
      <>
        <p
          className={
            result.success
              ? "font-inter-normal-12px text-gray-600"
              : "font-inter-normal-12px text-red-600"
          }
        >
          {result.message}
          {summary ? ` · ${summary}` : null}
        </p>
        {detailEntries.length > 0 && (
          <dl className="mt-2 space-y-1 rounded-lg bg-gray-50 p-3">
            {detailEntries.map(([key, value]) => (
              <div
                key={key}
                className="flex justify-between gap-2 font-inter-normal-12px"
              >
                <dt className="shrink-0 text-gray-500">{key}:</dt>
                <dd className="break-all text-right text-gray-800">
                  {formatDetailValue(value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </>
    );
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="pt-0.5">
        <StatusIcon state={state} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-inter-semibold-13px text-gray-800">
          {ENVIRONMENT_LABEL[environment]}
        </p>
        {target && (
          <p className="mt-0.5 break-all font-inter-normal-12px text-gray-500">
            {target}
          </p>
        )}
        <div className="mt-1">{renderResultLine()}</div>
      </div>
    </div>
  );
};

export const TestCredentialModal: React.FC<Props> = ({
  credential,
  onClose,
}) => {
  const testCredential = useTestCredentialById();
  const [results, setResults] = useState<
    Partial<Record<CredentialTestEnvironment, EnvironmentTestState>>
  >({});

  const runTest = useCallback(
    (
      credentialId: string,
      environment: CredentialTestEnvironment,
      isActive: () => boolean,
    ) => {
      setResults((prev) => ({ ...prev, [environment]: { status: "pending" } }));
      return testCredential
        .mutateAsync({ credentialId, environment })
        .then((result) => {
          // Ignore responses for a credential the modal has since switched away from
          if (!isActive()) return;
          setResults((prev) => ({
            ...prev,
            [environment]: { status: "success", result },
          }));
        })
        .catch((err: Error) => {
          if (!isActive()) return;
          setResults((prev) => ({
            ...prev,
            [environment]: {
              status: "error",
              message: err?.message ?? "An unexpected error occurred",
            },
          }));
        });
    },
    [testCredential.mutateAsync],
  );

  useEffect(() => {
    if (!credential) {
      return;
    }
    let active = true;
    setResults({});
    const environments: CredentialTestEnvironment[] = hasSandboxCredentials(
      credential,
    )
      ? ["production", "sandbox"]
      : ["production"];
    environments.forEach((env) => runTest(credential.id, env, () => active));
    return () => {
      active = false;
    };
  }, [credential, runTest]);

  const credentialName = credential?.name ?? "Unknown";
  const environmentsToShow: CredentialTestEnvironment[] = hasSandboxCredentials(
    credential,
  )
    ? ["production", "sandbox"]
    : ["production"];

  return (
    <Modal open={credential !== undefined} size="sm" onClose={onClose}>
      <Modal.Header>Test "{credentialName}" credentials</Modal.Header>
      <Modal.Content>
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {environmentsToShow.map((env) => {
            const config: EnvCredentialConfig | undefined =
              env === "production"
                ? credential?.env_configs?.live
                : credential?.env_configs?.sandbox;
            const target = credential
              ? describeTestTarget(credential.provider, config)
              : null;
            return (
              <EnvironmentRow
                key={env}
                environment={env}
                state={results[env] ?? { status: "pending" }}
                target={target}
              />
            );
          })}
        </div>
      </Modal.Content>
      <Modal.Footer primaryButton={false} secondaryButton="Close" />
    </Modal>
  );
};
